import { create } from 'zustand';
import {
  NODE_EXPLORER_WIDTH_KEY,
  NODE_EXPLORER_PANEL_MIN_WIDTH,
  NODE_EXPLORER_PANEL_MAX_WIDTH,
  DEFAULT_NODE_EXPLORER_PANEL_WIDTH,
  readStoredWidth,
} from '@/constants/workflow-editor';

type NodeExplorerTab = 'nodes' | 'chat';

export interface SourceChatContext {
  workflow_id: string;
  workflow_version_id: string;
  run_id: string;
  node_id: string;
  selected_file: string;
  selected_line_start?: number;
  selected_line_end?: number;
}

interface NodeExplorerState {
  isOpen: boolean;
  activeTab: NodeExplorerTab;
  isMaximized: boolean;
  isHoveringWhileMinimized: boolean;
  panelSize: number; // Fixed pixel width (persisted to localStorage)
  sourceChatContext: SourceChatContext | null;
  // Actions
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  setActiveTab: (tab: NodeExplorerTab) => void;
  openChatTab: () => void;
  openSourceChat: (context: SourceChatContext) => void;
  clearSourceChatContext: () => void;
  setMaximized: (maximized: boolean) => void;
  toggleMaximized: () => void;
  setIsHoveringWhileMinimized: (hovering: boolean) => void;
  setPanelSize: (px: number) => void;
  reset: () => void;
}

export const useNodeExplorerStore = create<NodeExplorerState>((set) => ({
  isOpen: false,
  activeTab: 'nodes',
  isMaximized: false,
  isHoveringWhileMinimized: false,
  panelSize: readStoredWidth(
    NODE_EXPLORER_WIDTH_KEY,
    DEFAULT_NODE_EXPLORER_PANEL_WIDTH,
  ),
  sourceChatContext: null,

  openSidebar: () => set({ isOpen: true }),
  closeSidebar: () =>
    set({ isOpen: false, isMaximized: false, isHoveringWhileMinimized: false }),
  toggleSidebar: () => set((state) => ({ isOpen: !state.isOpen })),
  setActiveTab: (tab) => set({ activeTab: tab }),
  openChatTab: () =>
    set({ isOpen: true, activeTab: 'chat', isMaximized: true }),
  openSourceChat: (sourceChatContext) =>
    set({
      isOpen: true,
      activeTab: 'chat',
      isMaximized: true,
      sourceChatContext,
    }),
  clearSourceChatContext: () => set({ sourceChatContext: null }),
  setMaximized: (maximized) =>
    set({
      isMaximized: maximized,
      isHoveringWhileMinimized: false,
    }),
  toggleMaximized: () =>
    set((state) => ({
      isMaximized: !state.isMaximized,
      isHoveringWhileMinimized: false,
    })),
  setIsHoveringWhileMinimized: (hovering) =>
    set({ isHoveringWhileMinimized: hovering }),
  setPanelSize: (px) => {
    const clamped = Math.round(
      Math.max(
        NODE_EXPLORER_PANEL_MIN_WIDTH,
        Math.min(NODE_EXPLORER_PANEL_MAX_WIDTH, px),
      ),
    );
    try {
      localStorage.setItem(NODE_EXPLORER_WIDTH_KEY, String(clamped));
    } catch {
      // ignore
    }
    set({ panelSize: clamped });
  },
  reset: () =>
    set({
      activeTab: 'nodes',
      isHoveringWhileMinimized: false,
      sourceChatContext: null,
    }),
}));
